
import { useState, useRef, useEffect, ReactNode } from "react";
import { Upload, X, Image as ImageIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { LazyImage } from "@/components/ui/lazy-image";
import { CropDialog } from "./CropDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Update the interface to include the maxFileSizeMB property
export interface ImageUploaderProps {
  onImageUploaded: (imagePath: string, file?: File) => void;
  currentImage?: string;
  label?: string;
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number;
  children?: ReactNode;
  maxFileSizeMB?: number;
  useCloudinary?: boolean;
}

export function ImageUploader({ 
  onImageUploaded, 
  currentImage, 
  label = "Featured Image",
  maxWidth = 1200,
  maxHeight = 1200,
  aspectRatio = 1,
  children,
  maxFileSizeMB = 5,
  useCloudinary = false
}: ImageUploaderProps) {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [dragActive, setDragActive] = useState(false);
  const [existingImages, setExistingImages] = useState<Array<{ name: string, url: string }>>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchExistingImages = async () => {
    if (!user) {
      toast.error("You need to be logged in to view your images");
      return;
    }

    setIsLoadingImages(true);
    try {
      const { data, error } = await supabase.storage
        .from("images")
        .list(user.id, {
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        throw error;
      }

      const imageList = await Promise.all(
        data
          .filter(file => file.name.match(/\.(jpeg|jpg|gif|png|webp)$/i))
          .map(async (file) => {
            const { data: urlData } = supabase.storage
              .from("images")
              .getPublicUrl(`${user.id}/${file.name}`);
            
            return {
              name: file.name,
              url: urlData.publicUrl
            };
          })
      );

      setExistingImages(imageList);
    } catch (error: any) {
      console.error("Error fetching images:", error);
      toast.error(`Failed to load images: ${error.message}`);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleFileSelection(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleFileSelection(file);
    }
  };

  const handleFileSelection = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPG, PNG, GIF, etc.)");
      return;
    }

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      toast.error(`Image is too large. Please upload an image smaller than ${maxFileSizeMB}MB`);
      return;
    }
    
    setSelectedFile(file);
    setCropDialogOpen(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    setCropDialogOpen(false);
    await uploadFile(croppedFile);
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setSelectedFile(null);
  };

  const uploadFile = async (file: File) => {
    if (!user) {
      toast.error("You need to be logged in to upload images");
      return;
    }

    setIsUploading(true);

    try {
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      const fileExt = file.name.split(".").pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error } = await supabase.storage
        .from("images")
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      const { data } = supabase.storage
        .from("images")
        .getPublicUrl(filePath);

      onImageUploaded(data.publicUrl, file);
      toast.success("Image uploaded successfully!");
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast.error(`Upload failed: ${error.message}`);
      setPreview(currentImage || null);
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setPreview(null);
    onImageUploaded("", undefined);
  };

  const selectExistingImage = (imageUrl: string) => {
    setPreview(imageUrl);
    onImageUploaded(imageUrl, undefined);
    setIsDialogOpen(false);
    toast.success("Image selected successfully!");
  };

  return (
    <div className="space-y-2">
      {label && <Label htmlFor="image-upload">{label}</Label>}
      
      {preview ? (
        <div className="relative rounded-md overflow-hidden border border-input">
          <div className={`aspect-${aspectRatio === 1 ? 'square' : aspectRatio === 16/9 ? '[16/9]' : '[4/3]'} w-full h-auto overflow-hidden`}>
            <LazyImage 
              src={preview} 
              alt="Preview" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-2 right-2 flex gap-2">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={() => fetchExistingImages()}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Select an image</DialogTitle>
                </DialogHeader>
                {isLoadingImages ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : existingImages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ImageIcon className="mx-auto h-12 w-12 opacity-20 mb-2" />
                    <p>No images found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 py-4">
                    {existingImages.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => selectExistingImage(image.url)}
                        className={`aspect-${aspectRatio === 1 ? 'square' : aspectRatio === 16/9 ? '[16/9]' : '[4/3]'} border rounded-md overflow-hidden cursor-pointer hover:border-primary transition-colors`}
                      >
                        <LazyImage
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={removeImage}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {children ? (
            <DialogTrigger asChild>
              {children}
            </DialogTrigger>
          ) : (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${
                dragActive 
                  ? "border-primary bg-primary/5" 
                  : "border-input hover:border-primary/50"
              }`}
            >
              <Input
                ref={fileInputRef}
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </div>
              ) : (
                <>
                  <div className="mb-2 p-2 bg-primary/10 rounded-full">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  <p className="font-medium text-center">Click to choose an image or drag and drop</p>
                  <p className="text-sm text-muted-foreground text-center mt-1">
                    JPG, PNG or GIF • Max {maxFileSizeMB}MB
                  </p>
                </>
              )}
            </div>
          )}

          {!children && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fetchExistingImages()}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Select from existing images
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Select an image</DialogTitle>
                </DialogHeader>
                {isLoadingImages ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : existingImages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ImageIcon className="mx-auto h-12 w-12 opacity-20 mb-2" />
                    <p>No images found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 py-4">
                    {existingImages.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => selectExistingImage(image.url)}
                        className={`aspect-${aspectRatio === 1 ? 'square' : aspectRatio === 16/9 ? '[16/9]' : '[4/3]'} border rounded-md overflow-hidden cursor-pointer hover:border-primary transition-colors`}
                      >
                        <LazyImage
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}
      
      <CropDialog
        open={cropDialogOpen}
        onOpenChange={setCropDialogOpen}
        imageFile={selectedFile}
        onCropComplete={handleCropComplete}
        onCancel={handleCropCancel}
        aspectRatio={aspectRatio}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
      />
    </div>
  );
}
